/**
 * Application store (zustand). Holds the project list, session-uploaded
 * models (the contract has no list-models endpoint yet — TODO-Wave3), and
 * per-model spaces/readiness fetched from the API.
 *
 * Space edits are optimistic: the row updates immediately, then the PATCH
 * response replaces it; on failure the previous row is restored and the
 * error is surfaced via `spacePatchError`.
 */
import { create } from "zustand";

import * as api from "../api/client";
import type { OccupancyVocabEntry } from "../api/client";
import type {
  ApiProject,
  ApiSpace,
  AutoAssignOptions,
  AutoAssignResult,
  ReadinessReport,
  SpacePatch,
} from "../api/types";
import type { ModelSummary } from "../viewer/ifc/types";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

/** A model uploaded in this browser session. */
export interface SessionModel {
  modelId: string;
  projectId: string;
  fileName: string;
  summary: ModelSummary;
  /**
   * Whether a renderable 3D (Fragments) artifact exists for this model —
   * `false` when the worker's Fragments conversion failed during upload
   * (`UploadedModel.fragments === null`; see `api/modelUpload.ts`). The
   * summary/2D data (readiness, wizard, plan view) is still fully valid and
   * usable in that case — this flag exists ONLY so the degraded 3D-viewer
   * capability is visible in the UI instead of looking identical to a fully
   * successful upload. `undefined` (omitted by older callers/tests) is
   * treated as "unknown/available" — never surfaced as a false warning.
   */
  threeDAvailable?: boolean;
}

export interface IstariState {
  projects: ApiProject[];
  projectsStatus: LoadStatus;
  projectsError: string | null;

  /** Session-scoped (no list-models endpoint in the Wave 2 contract). */
  models: SessionModel[];

  /** Keyed by model id. */
  spaces: Record<string, ApiSpace[]>;
  spacesStatus: Record<string, LoadStatus>;
  readiness: Record<string, ReadinessReport>;
  readinessStatus: Record<string, LoadStatus>;

  /** Last PATCH failure message, cleared on the next successful edit. */
  spacePatchError: string | null;

  /**
   * Canonical occupancy vocabulary fetched from `GET /api/vocab/occupancy`.
   * `null` until the first successful fetch; consumers should render a
   * loading state until then. See ADR 0010.
   */
  occupancyVocab: OccupancyVocabEntry[] | null;
  occupancyVocabError: string | null;

  /**
   * Ruleset-scoped occupancy vocabulary fetched from
   * `GET /api/vocab/occupancy?ruleset=<id>` (ENG-14678 / ADR 0011). Keyed by
   * ruleset id; `null` until the first successful fetch for that ruleset.
   * The wizard's secondary subtype picker reads this.
   */
  occupancyVocabByRuleset: Record<string, OccupancyVocabEntry[] | null>;

  loadProjects: () => Promise<void>;
  /**
   * Fetch + cache the occupancy vocabulary. Idempotent: subsequent calls
   * are no-ops once the vocab is loaded.
   */
  loadOccupancyVocab: () => Promise<void>;
  /**
   * Fetch + cache the ruleset-scoped occupancy vocabulary (with embedded
   * subtypes). Idempotent per ruleset id.
   */
  loadOccupancyVocabForRuleset: (rulesetId: string) => Promise<void>;
  createProject: (name: string) => Promise<ApiProject>;
  registerModel: (model: SessionModel) => void;
  loadSpaces: (modelId: string) => Promise<void>;
  loadReadiness: (modelId: string) => Promise<void>;
  /**
   * Optimistically apply `patch` to one space, then PATCH the server.
   * Returns true on success; on failure reverts the row and returns false.
   */
  patchSpace: (modelId: string, spaceId: string, patch: SpacePatch) => Promise<boolean>;
  /**
   * One-click demo helper: bulk auto-assign occupancy and/or exits on the
   * server, then replace the local spaces + readiness with the response.
   * Returns the result, or null on failure (error in `spacePatchError`).
   */
  autoAssign: (modelId: string, options?: AutoAssignOptions) => Promise<AutoAssignResult | null>;
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Apply only the keys actually present on the patch (null clears a field). */
export function applySpacePatch(space: ApiSpace, patch: SpacePatch): ApiSpace {
  const next = { ...space };
  if ("occupancy_override" in patch) {
    next.occupancy_override = patch.occupancy_override ?? null;
  }
  if (patch.is_exit !== undefined) next.is_exit = patch.is_exit;
  if (patch.exclusion !== undefined) next.exclusion = patch.exclusion;
  if ("ambient_dba" in patch) next.ambient_dba = patch.ambient_dba ?? null;
  if (patch.subtypes !== undefined) next.subtypes = { ...patch.subtypes };
  return next;
}

function replaceSpace(
  rows: ApiSpace[] | undefined,
  spaceId: string,
  replacement: ApiSpace,
): ApiSpace[] {
  return (rows ?? []).map((s) => (s.id === spaceId ? replacement : s));
}

export const useIstariStore = create<IstariState>((set, get) => ({
  projects: [],
  projectsStatus: "idle",
  projectsError: null,
  models: [],
  spaces: {},
  spacesStatus: {},
  readiness: {},
  readinessStatus: {},
  spacePatchError: null,
  occupancyVocab: null,
  occupancyVocabError: null,
  occupancyVocabByRuleset: {},

  loadProjects: async () => {
    set({ projectsStatus: "loading", projectsError: null });
    try {
      const projects = await api.listProjects();
      set({ projects, projectsStatus: "ready" });
    } catch (err) {
      set({ projectsStatus: "error", projectsError: message(err) });
    }
  },

  loadOccupancyVocab: async () => {
    if (get().occupancyVocab) return;
    try {
      const items = await api.getOccupancyVocab();
      set({ occupancyVocab: items, occupancyVocabError: null });
    } catch (err) {
      set({ occupancyVocabError: message(err) });
    }
  },

  loadOccupancyVocabForRuleset: async (rulesetId) => {
    if (get().occupancyVocabByRuleset[rulesetId]) return;
    try {
      const items = await api.getOccupancyVocab(rulesetId);
      set((state) => ({
        occupancyVocabByRuleset: {
          ...state.occupancyVocabByRuleset,
          [rulesetId]: items,
        },
      }));
    } catch (err) {
      // Best-effort: log and leave the slot null so a retry can re-fetch.
      console.error(`Failed to load occupancy vocab for ruleset ${rulesetId}`, err);
    }
  },

  createProject: async (name) => {
    const project = await api.createProject(name);
    set((state) => ({ projects: [...state.projects, project] }));
    return project;
  },

  registerModel: (model) => {
    set((state) => ({
      models: [...state.models.filter((m) => m.modelId !== model.modelId), model],
    }));
  },

  loadSpaces: async (modelId) => {
    set((state) => ({
      spacesStatus: { ...state.spacesStatus, [modelId]: "loading" },
    }));
    try {
      const rows = await api.getSpaces(modelId);
      set((state) => ({
        spaces: { ...state.spaces, [modelId]: rows },
        spacesStatus: { ...state.spacesStatus, [modelId]: "ready" },
      }));
    } catch {
      set((state) => ({
        spacesStatus: { ...state.spacesStatus, [modelId]: "error" },
      }));
    }
  },

  loadReadiness: async (modelId) => {
    set((state) => ({
      readinessStatus: { ...state.readinessStatus, [modelId]: "loading" },
    }));
    try {
      const report = await api.getReadiness(modelId);
      set((state) => ({
        readiness: { ...state.readiness, [modelId]: report },
        readinessStatus: { ...state.readinessStatus, [modelId]: "ready" },
      }));
    } catch {
      set((state) => ({
        readinessStatus: { ...state.readinessStatus, [modelId]: "error" },
      }));
    }
  },

  autoAssign: async (modelId, options = {}) => {
    try {
      const result = await api.autoAssignSpaces(modelId, options);
      set((state) => ({
        spacePatchError: null,
        spaces: { ...state.spaces, [modelId]: result.spaces },
        spacesStatus: { ...state.spacesStatus, [modelId]: "ready" },
        readiness: { ...state.readiness, [modelId]: result.readiness },
        readinessStatus: { ...state.readinessStatus, [modelId]: "ready" },
      }));
      return result;
    } catch (err) {
      set({ spacePatchError: message(err) });
      return null;
    }
  },

  patchSpace: async (modelId, spaceId, patch) => {
    const previous = get().spaces[modelId]?.find((s) => s.id === spaceId);
    if (!previous) return false;

    // Optimistic apply. Keep the exact row object so a late failure can tell
    // whether the row still holds OUR optimistic value or a newer edit's.
    const optimistic = applySpacePatch(previous, patch);
    set((state) => ({
      spacePatchError: null,
      spaces: {
        ...state.spaces,
        [modelId]: replaceSpace(state.spaces[modelId], spaceId, optimistic),
      },
    }));

    try {
      const confirmed = await api.patchSpace(modelId, spaceId, patch);
      set((state) => ({
        spaces: {
          ...state.spaces,
          [modelId]: replaceSpace(state.spaces[modelId], spaceId, confirmed),
        },
      }));
      return true;
    } catch (err) {
      // Compare-and-revert: restore the pre-patch row ONLY if it still holds
      // the optimistic value we set — never clobber a newer concurrent edit.
      set((state) => {
        const rows = state.spaces[modelId];
        const stillOurs = rows?.find((s) => s.id === spaceId) === optimistic;
        return {
          spacePatchError: message(err),
          spaces: stillOurs
            ? { ...state.spaces, [modelId]: replaceSpace(rows, spaceId, previous) }
            : state.spaces,
        };
      });
      return false;
    }
  },
}));
